import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Globe, Users, Shield, CheckCircle, BadgeCheck, Quote } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";
import Footer from "../components/Footer";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const handleCTA = () => {
    // Check if user already exists
    const existingUser = localStorage.getItem("havns_user");
    if (existingUser) {
      navigate("/discover");
    } else {
      navigate("/get-started");
    }
  };

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      quote: "Finally, a community where I can travel without awkward romantic expectations. Met amazing people for my Japan trip!",
      name: "Meera S.",
      role: "Product Designer, Bangalore",
    },
    {
      quote: "Havns gets it. I'm happy being single, I just want friends to explore the world with. Found my trekking buddies here.",
      name: "Arjun K.",
      role: "Startup Founder, Mumbai",
    },
    {
      quote: "Coffee meetups, weekend treks, museum visits—all platonic, all genuine. This is what I was looking for.",
      name: "Priya R.",
      role: "Finance Lead, Delhi",
    },
  ];

  const memberProfiles = [
    {
      image: "https://images.unsplash.com/photo-1770627016447-cb9d29ed0398?w=600&h=900&fit=crop&crop=faces",
      name: "Anjali",
      age: 29,
      profession: "Architect",
      city: "Mumbai",
      prompt1Label: "My solo travel bucket list",
      prompt1Value: "Japan in cherry blossom season, Northern Lights in Iceland",
      prompt2Label: "Let's meet for",
      prompt2Value: "Coffee & architecture walks",
    },
    {
      image: "https://images.unsplash.com/photo-1650110002977-3ee8cc5eac91?w=600&h=900&fit=crop&crop=faces",
      name: "Rahul",
      age: 34,
      profession: "Tech Lead",
      city: "Bangalore",
      prompt1Label: "Professional expertise",
      prompt1Value: "AI & Machine Learning",
      prompt2Label: "Let's meet for",
      prompt2Value: "Weekend treks & tech talks",
    },
    {
      image: "https://images.unsplash.com/photo-1762341115993-8b949d576a10?w=600&h=900&fit=crop&crop=faces",
      name: "Priya",
      age: 31,
      profession: "Finance Director",
      city: "Delhi",
      prompt1Label: "My solo travel bucket list",
      prompt1Value: "Wine tasting in Tuscany, Safari in Kenya",
      prompt2Label: "Let's meet for",
      prompt2Value: "Art galleries & book clubs",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBF9F7]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          
          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-home-link"
            >
              Home
            </Link>
            <Link 
              to="/philosophy" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-philosophy-link"
            >
              Philosophy
            </Link>
            <Link 
              to="/movement" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-movement-link"
            >
              Movement
            </Link>
            <Link 
              to="/stories" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
              data-testid="nav-stories-link"
            >
              Stories
            </Link>
          </div>

          <Button
            onClick={handleCTA}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6 text-sm font-medium tracking-wide"
            data-testid="nav-apply-btn"
          >
            {user ? "Enter" : "Get Started"}
          </Button>
        </div>
      </nav>

      {/* Hero Section - Hinge Style */}
      <section className="min-h-screen flex items-center pt-20 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="order-2 lg:order-1">
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                An Anti-Dating Community
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1A1A1A] leading-[1.1] mb-8">
                Find your<br />
                <span className="italic">people.</span><br />
                Not your person.
              </h1>
              <p className="text-xl text-[#666] leading-relaxed mb-10 max-w-lg">
                Havns is where single-by-choice professionals find travel buddies, 
                coffee companions, and genuine friendships—without the dating drama.
              </p>
              <Button
                onClick={handleCTA}
                className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-10 py-7 text-base font-medium tracking-wide inline-flex items-center gap-3"
                data-testid="hero-cta-btn"
              >
                Join Havns
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Right: Hero Image */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1753351056838-143bc3e4cf03?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                    alt="Friends at cafe"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl max-w-[280px]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#C5B358]/20 flex items-center justify-center">
                      <BadgeCheck className="w-5 h-5 text-[#C5B358]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Verified Members</p>
                      <p className="text-sm text-[#666]">Aadhaar KYC verified</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Minimal */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-6">
              Three simple steps.
            </h2>
            <p className="text-xl text-[#666] max-w-2xl mx-auto">
              Join a community of verified professionals in minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FBF9F7] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-[#1A1A1A]">1</span>
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">Sign up & Pay</h3>
              <p className="text-[#666]">
                Quick Google signup and ₹999 membership fee
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FBF9F7] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-[#1A1A1A]">2</span>
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">Verify Identity</h3>
              <p className="text-[#666]">
                Complete Aadhaar KYC for trust & safety
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FBF9F7] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-[#1A1A1A]">3</span>
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">Start Connecting</h3>
              <p className="text-[#666]">
                Join journeys and meetups with verified soloists
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Member Profiles - Hinge Style Cards */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-6">
              Who's inside?
            </h2>
            <p className="text-xl text-[#666] max-w-2xl mx-auto">
              Verified professionals who value their independence. Faces revealed after you join.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 justify-center items-center lg:items-stretch">
            {memberProfiles.map((profile, idx) => (
              <div key={idx} className="w-full max-w-[340px]">
                <div className="bg-white rounded-[1.5rem] overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                  {/* Blurred Image */}
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img
                      src={profile.image}
                      alt="Member"
                      className="w-full h-full object-cover blur-sm scale-110"
                    />
                    {/* Overlay with silhouette hint */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/40 to-[#1A1A1A]/20" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/20 flex items-center justify-center">
                        <Users className="w-10 h-10 text-white/60" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white text-xl font-serif">
                          {profile.name.charAt(0)}***, {profile.age}
                        </h3>
                        <BadgeCheck className="w-5 h-5 text-[#C5B358]" />
                      </div>
                      <p className="text-white/80 text-sm">
                        {profile.profession} • {profile.city}
                      </p>
                    </div>
                  </div>
                  
                  {/* Prompts - visible */}
                  <div className="p-5 space-y-4">
                    <div className="bg-[#FBF9F7] rounded-xl p-4">
                      <p className="text-xs uppercase tracking-wider text-[#C5B358] mb-1">
                        {profile.prompt1Label}
                      </p>
                      <p className="text-[#1A1A1A]">{profile.prompt1Value}</p>
                    </div>
                    <div className="bg-[#FBF9F7] rounded-xl p-4">
                      <p className="text-xs uppercase tracking-wider text-[#C5B358] mb-1">
                        {profile.prompt2Label}
                      </p>
                      <p className="text-[#1A1A1A]">{profile.prompt2Value}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button
              onClick={handleCTA}
              className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-10 py-7 text-base font-medium tracking-wide inline-flex items-center gap-3"
              data-testid="profiles-cta-btn"
            >
              Unlock Full Profiles
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials - Hinge Style */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-4">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase">
              What Our Members Say
            </p>
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <div className="relative min-h-[200px]">
              {testimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-all duration-500 ${
                    idx === activeTestimonial 
                      ? "opacity-100 translate-y-0" 
                      : "opacity-0 translate-y-4 pointer-events-none"
                  }`}
                >
                  <blockquote className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] leading-relaxed mb-8">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-[#666]">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-12">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === activeTestimonial ? "bg-[#1A1A1A] w-6" : "bg-[#DDD]"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
                The No-Dating Manifesto
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-10">
                We connect<br />
                <span className="italic">differently.</span>
              </h2>
              <div className="space-y-5">
                {[
                  "We value independence and personal growth",
                  "We seek meaningful platonic connections",
                  "We respect boundaries without exception",
                  "We report romantic advances immediately",
                  "We build a community of trust",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <CheckCircle className="w-5 h-5 text-[#C5B358] flex-shrink-0" />
                    <p className="text-white/80 text-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="aspect-square rounded-[2rem] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1753351056838-143bc3e4cf03?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Friends at cafe"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Membership
            </p>
            <h2 className="font-serif text-5xl sm:text-6xl text-[#1A1A1A] mb-4">
              ₹999<span className="text-2xl text-[#666] font-sans">/month</span>
            </h2>
            
            <div className="bg-white rounded-2xl p-6 mb-8 text-left max-w-md mx-auto">
              <p className="text-[#666]">
                <span className="text-[#1A1A1A] font-medium">Why we charge: </span>
                This fee keeps out bots and casual users. Combined with Aadhaar verification, 
                your membership supports a curated, ad-free community.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="bg-white rounded-full px-5 py-2 text-sm text-[#1A1A1A]">
                <BadgeCheck className="w-4 h-4 inline mr-2 text-[#C5B358]" />
                Verified Network
              </div>
              <div className="bg-white rounded-full px-5 py-2 text-sm text-[#1A1A1A]">
                <Globe className="w-4 h-4 inline mr-2 text-[#C5B358]" />
                Unlimited Journeys
              </div>
              <div className="bg-white rounded-full px-5 py-2 text-sm text-[#1A1A1A]">
                <Users className="w-4 h-4 inline mr-2 text-[#C5B358]" />
                All Meetups
              </div>
            </div>

            <Button
              onClick={handleCTA}
              className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-12 py-7 text-base font-medium tracking-wide inline-flex items-center gap-3"
              data-testid="pricing-cta-btn"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-[#999] text-sm mt-4">No free trials. High-intent members only.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
